import Divider from "@material-ui/core/Divider";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import Paper from "@material-ui/core/Paper";
import {
  createMuiTheme,
  makeStyles,
  ThemeProvider
} from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import FormatBoldIcon from "@material-ui/icons/FormatBold";
import HelpIcon from "@material-ui/icons/Help";
import { useFormik } from "formik";
import FormatColorHighlightIcon from "mdi-material-ui/FormatColorHighlight";
import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import * as yup from "yup";

import HelpAdornment from "../../../InputAdornments/Help";
import * as constants from "../../../../constants";
import MarkdownHelpDialog from "../../MarkdownHelp";

const darkTheme = createMuiTheme({
  palette: {
    type: "dark"
  }
});

const useStyles = makeStyles(theme => ({
  code: {
    backgroundColor: "yellow"
  },
  formatToolbar: {
    display: "flex",
    alignItems: "center",
    padding: 8
  },
  formatToolbarContainer: {
    position: "absolute",
    bottom: -54,
    right: 0
  },
  formatToolbarTitle: {
    paddingLeft: 8,
    paddingRight: 16
  },
  formatToolbarIconSeparator: {
    width: 4
  },
  previewTitleContainer: {
    display: "flex",
    alignItems: "center"
  },
  previewTitleSeparator: {
    width: 8
  },
  separator: {
    height: 8
  },
  textFieldContainer: {
    position: "relative"
  }
}));

const StyledCode = props => {
  const classes = useStyles();
  return <code className={classes.code}>{props.children}</code>;
};

const Notes = ({ property, disabled, onEdit, onSubmit }) => {
  const adminNoteInputRef = useRef(null);
  const serviceProviderNoteInputRef = useRef(null);
  const formik = useFormik({
    initialValues: {
      adminNote: property && property.adminNote ? property.adminNote : "",
      serviceProviderNote:
        property && property.serviceProviderNote
          ? property.serviceProviderNote
          : ""
    },
    validationSchema: yup.object({
      adminNote: yup.string(),
      serviceProviderNote: yup.string()
    }),
    onSubmit: async values => {
      onSubmit(
        {
          propertyId: property.propertyId,
          adminNote: values.adminNote ? values.adminNote : null,
          serviceProviderNote: values.serviceProviderNote
            ? values.serviceProviderNote
            : null
        },
        property => {
          formik.resetForm({
            values: {
              adminNote: property.adminNote ? property.adminNote : "",
              serviceProviderNote: property.serviceProviderNote
                ? property.serviceProviderNote
                : ""
            }
          });
        }
      );
    }
  });
  const [
    serviceProviderNoteStylesVisible,
    setServiceProviderNoteStylesVisible
  ] = useState(false);
  const [adminNoteStylesVisible, setAdminNoteStylesVisible] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  useEffect(() => {
    if (formik.dirty) {
      const resetForm = property => {
        formik.resetForm({
          values: {
            adminNote: property.adminNote ? property.adminNote : "",
            serviceProviderNote: property.serviceProviderNote
              ? property.serviceProviderNote
              : ""
          }
        });
      };
      onEdit(formik.handleSubmit, resetForm);
    }
  }, [formik.dirty]);
  useEffect(() => {
    if (!formik.dirty) {
      formik.resetForm({
        values: {
          adminNote: property.adminNote ? property.adminNote : "",
          serviceProviderNote: property.serviceProviderNote
            ? property.serviceProviderNote
            : ""
        }
      });
    }
  }, [property]);
  const classes = useStyles();
  const toggleAdminNoteBold = () => {
    if (adminNoteInputRef.current) {
      const selection = {
        end: adminNoteInputRef.current.selectionEnd,
        start: adminNoteInputRef.current.selectionStart
      };
      const { adminNote } = formik.values;
      if (
        adminNote.charAt(selection.start - 1) === "*" &&
        adminNote.charAt(selection.start - 2) === "*" &&
        adminNote.charAt(selection.end) === "*" &&
        adminNote.charAt(selection.end + 1)
      ) {
        const nextValue = `${adminNote.slice(
          0,
          selection.start - 2
        )}${adminNote.slice(selection.start, selection.end)}${adminNote.slice(
          selection.end + 2
        )}`;
        formik.setFieldValue("adminNote", nextValue);
        adminNoteInputRef.current.value = nextValue;
        adminNoteInputRef.current.selectionEnd = selection.end - 2;
        adminNoteInputRef.current.selectionStart = selection.start - 2;
      } else {
        let nextValue;
        if (selection.start !== selection.end) {
          nextValue = `${adminNote.slice(
            0,
            selection.start
          )}**${adminNote.slice(
            selection.start,
            selection.end
          )}**${adminNote.slice(selection.end)}`;
          formik.setFieldValue("adminNote", nextValue);
        } else {
          nextValue = `${adminNote.slice(
            0,
            selection.start
          )}****${adminNote.slice(selection.start)}`;
          formik.setFieldValue("adminNote", nextValue);
        }
        adminNoteInputRef.current.value = nextValue;
        adminNoteInputRef.current.selectionEnd = selection.end + 2;
        adminNoteInputRef.current.selectionStart = selection.start + 2;
      }
    }
  };
  const toggleAdminNoteHighlight = () => {
    if (adminNoteInputRef.current) {
      const selection = {
        end: adminNoteInputRef.current.selectionEnd,
        start: adminNoteInputRef.current.selectionStart
      };
      const { adminNote } = formik.values;
      if (
        adminNote.charAt(selection.start - 1) === "`" &&
        adminNote.charAt(selection.end) === "`"
      ) {
        const nextValue = `${adminNote.slice(
          0,
          selection.start - 1
        )}${adminNote.slice(selection.start, selection.end)}${adminNote.slice(
          selection.end + 1
        )}`;
        formik.setFieldValue("adminNote", nextValue);
        adminNoteInputRef.current.value = nextValue;
        adminNoteInputRef.current.selectionEnd = selection.end - 1;
        adminNoteInputRef.current.selectionStart = selection.start - 1;
      } else {
        let nextValue;
        if (selection.start !== selection.end) {
          nextValue = `${adminNote.slice(
            0,
            selection.start
          )}\`${adminNote.slice(
            selection.start,
            selection.end
          )}\`${adminNote.slice(selection.end)}`;
          formik.setFieldValue("adminNote", nextValue);
        } else {
          nextValue = `${adminNote.slice(
            0,
            selection.start
          )}\`\`${adminNote.slice(selection.start)}`;
          formik.setFieldValue("adminNote", nextValue);
        }
        adminNoteInputRef.current.value = nextValue;
        adminNoteInputRef.current.selectionEnd = selection.end + 1;
        adminNoteInputRef.current.selectionStart = selection.start + 1;
      }
    }
  };
  const toggleServiceProviderNoteBold = () => {
    if (serviceProviderNoteInputRef.current) {
      const selection = {
        end: serviceProviderNoteInputRef.current.selectionEnd,
        start: serviceProviderNoteInputRef.current.selectionStart
      };
      const { serviceProviderNote } = formik.values;
      if (
        serviceProviderNote.charAt(selection.start - 1) === "*" &&
        serviceProviderNote.charAt(selection.start - 2) === "*" &&
        serviceProviderNote.charAt(selection.end) === "*" &&
        serviceProviderNote.charAt(selection.end + 1)
      ) {
        const nextValue = `${serviceProviderNote.slice(
          0,
          selection.start - 2
        )}${serviceProviderNote.slice(
          selection.start,
          selection.end
        )}${serviceProviderNote.slice(selection.end + 2)}`;
        formik.setFieldValue("serviceProviderNote", nextValue);
        serviceProviderNoteInputRef.current.value = nextValue;
        serviceProviderNoteInputRef.current.selectionEnd = selection.end - 2;
        serviceProviderNoteInputRef.current.selectionStart =
          selection.start - 2;
      } else {
        let nextValue;
        if (selection.start !== selection.end) {
          nextValue = `${serviceProviderNote.slice(
            0,
            selection.start
          )}**${serviceProviderNote.slice(
            selection.start,
            selection.end
          )}**${serviceProviderNote.slice(selection.end)}`;
          formik.setFieldValue("serviceProviderNote", nextValue);
        } else {
          nextValue = `${serviceProviderNote.slice(
            0,
            selection.start
          )}****${serviceProviderNote.slice(selection.start)}`;
          formik.setFieldValue("serviceProviderNote", nextValue);
        }
        serviceProviderNoteInputRef.current.value = nextValue;
        serviceProviderNoteInputRef.current.selectionEnd = selection.end + 2;
        serviceProviderNoteInputRef.current.selectionStart =
          selection.start + 2;
      }
    }
  };
  const toggleServiceProviderNoteHighlight = () => {
    if (serviceProviderNoteInputRef.current) {
      const selection = {
        end: serviceProviderNoteInputRef.current.selectionEnd,
        start: serviceProviderNoteInputRef.current.selectionStart
      };
      const { serviceProviderNote } = formik.values;
      if (
        serviceProviderNote.charAt(selection.start - 1) === "`" &&
        serviceProviderNote.charAt(selection.end) === "`"
      ) {
        const nextValue = `${serviceProviderNote.slice(
          0,
          selection.start - 1
        )}${serviceProviderNote.slice(
          selection.start,
          selection.end
        )}${serviceProviderNote.slice(selection.end + 1)}`;
        formik.setFieldValue("serviceProviderNote", nextValue);
        serviceProviderNoteInputRef.current.value = nextValue;
        serviceProviderNoteInputRef.current.selectionEnd = selection.end - 1;
        serviceProviderNoteInputRef.current.selectionStart =
          selection.start - 1;
      } else {
        let nextValue;
        if (selection.start !== selection.end) {
          nextValue = `${serviceProviderNote.slice(
            0,
            selection.start
          )}\`${serviceProviderNote.slice(
            selection.start,
            selection.end
          )}\`${serviceProviderNote.slice(selection.end)}`;
          formik.setFieldValue("serviceProviderNote", nextValue);
        } else {
          nextValue = `${serviceProviderNote.slice(
            0,
            selection.start
          )}\`\`${serviceProviderNote.slice(selection.start)}`;
          formik.setFieldValue("serviceProviderNote", nextValue);
        }
        serviceProviderNoteInputRef.current.value = nextValue;
        serviceProviderNoteInputRef.current.selectionEnd = selection.end + 1;
        serviceProviderNoteInputRef.current.selectionStart =
          selection.start + 1;
      }
    }
  };
  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h5">Notes</Typography>
          <div className={classes.separator} />
          <Divider />
        </Grid>
        <Grid item xs={12}>
          <div className={classes.textFieldContainer}>
            <TextField
              disabled={disabled}
              fullWidth
              InputProps={{
                startAdornment: (
                  <HelpAdornment title={constants.helpText.propertyAdminNote} />
                )
              }}
              inputRef={adminNoteInputRef}
              label="Admin"
              multiline
              name="adminNote"
              onBlur={() => setAdminNoteStylesVisible(false)}
              onChange={formik.handleChange}
              onFocus={() => setAdminNoteStylesVisible(true)}
              value={formik.values.adminNote}
              variant="filled"
            />
            {adminNoteStylesVisible && (
              <div className={classes.formatToolbarContainer}>
                <ThemeProvider theme={darkTheme}>
                  <Paper elevation={4}>
                    <div className={classes.formatToolbar}>
                      <Typography className={classes.formatToolbarTitle}>
                        Styles
                      </Typography>
                      <IconButton
                        onMouseDown={event => {
                          event.preventDefault();
                          toggleAdminNoteHighlight();
                        }}
                        size="small"
                      >
                        <FormatColorHighlightIcon />
                      </IconButton>
                      <div className={classes.formatToolbarIconSeparator} />
                      <IconButton
                        onMouseDown={event => {
                          event.preventDefault();
                          toggleAdminNoteBold();
                        }}
                        size="small"
                      >
                        <FormatBoldIcon />
                      </IconButton>
                    </div>
                  </Paper>
                </ThemeProvider>
              </div>
            )}
          </div>
        </Grid>
        <Grid item xs={12}>
          <div className={classes.previewTitleContainer}>
            <IconButton onClick={() => setHelpOpen(true)} size="small">
              <HelpIcon />
            </IconButton>
            <div className={classes.previewTitleSeparator} />
            <Typography variant="overline">Preview</Typography>
          </div>
          <ReactMarkdown
            renderers={{ inlineCode: StyledCode }}
            source={formik.values.adminNote}
          />
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={12}>
          <div className={classes.textFieldContainer}>
            <TextField
              disabled={disabled}
              fullWidth
              InputProps={{
                startAdornment: (
                  <HelpAdornment
                    title={constants.helpText.propertyServiceProviderNote}
                  />
                )
              }}
              label="General"
              multiline
              name="serviceProviderNote"
              onBlur={() => setServiceProviderNoteStylesVisible(false)}
              onChange={formik.handleChange}
              onFocus={() => setServiceProviderNoteStylesVisible(true)}
              inputRef={serviceProviderNoteInputRef}
              value={formik.values.serviceProviderNote}
              variant="filled"
            />
            {serviceProviderNoteStylesVisible && (
              <div className={classes.formatToolbarContainer}>
                <ThemeProvider theme={darkTheme}>
                  <Paper elevation={4}>
                    <div className={classes.formatToolbar}>
                      <Typography className={classes.formatToolbarTitle}>
                        Styles
                      </Typography>
                      <IconButton
                        onMouseDown={event => {
                          event.preventDefault();
                          toggleServiceProviderNoteHighlight();
                        }}
                        size="small"
                      >
                        <FormatColorHighlightIcon />
                      </IconButton>
                      <div className={classes.formatToolbarIconSeparator} />
                      <IconButton
                        onMouseDown={event => {
                          event.preventDefault();
                          toggleServiceProviderNoteBold();
                        }}
                        size="small"
                      >
                        <FormatBoldIcon />
                      </IconButton>
                    </div>
                  </Paper>
                </ThemeProvider>
              </div>
            )}
          </div>
        </Grid>
        <Grid item xs={12}>
          <div className={classes.previewTitleContainer}>
            <IconButton onClick={() => setHelpOpen(true)} size="small">
              <HelpIcon />
            </IconButton>
            <div className={classes.previewTitleSeparator} />
            <Typography variant="overline">Preview</Typography>
          </div>
          <ReactMarkdown
            renderers={{ inlineCode: StyledCode }}
            source={formik.values.serviceProviderNote}
          />
        </Grid>
      </Grid>
      <MarkdownHelpDialog
        isOpen={helpOpen}
        onClose={() => setHelpOpen(false)}
      />
    </>
  );
};

export default Notes;
